<template>
  <div class="withdraw-account-page">
    <!-- 实名认证拦截弹窗 -->
    <div class="auth-modal" v-if="showAuthModal">
      <div class="auth-modal-content">
        <h3 class="auth-modal-title">实名认证提醒</h3>
        <p class="auth-modal-desc">
          您尚未完成实名认证，无法进行提现相关操作。<br>
          请先完善实名认证信息，完成后即可正常提现。<br>
          如有疑问，欢迎随时联系我们。
        </p>
        <button class="auth-modal-btn" @click="goToAuthPage">去完善</button>
      </div>
    </div>

    <!-- 忘记密码弹窗 -->
    <div class="forget-pwd-modal" v-if="showForgetPwdModal">
      <div class="forget-pwd-content">
        <h3 class="forget-pwd-title">忘记提现密码</h3>

        <!-- 步骤1：验证手机号 -->
        <div v-if="forgetStep === 1" class="forget-step-content">
          <div class="form-item">
            <label class="form-label">当前手机号: </label>
            <input type="text" class="form-input" v-model="forgetForm.phone" disabled placeholder="正在获取...">
          </div>
          <div class="form-item">
            <label class="form-label">验证码 <span class="required">*</span></label>
            <div class="code-input-group">
              <input type="text" class="form-input code-input" v-model="forgetForm.code" placeholder="请输入验证码">
              <button class="code-btn" :disabled="isSendingCode || countdown > 0" @click="handleSendCode">
                {{ countdown > 0 ? `${countdown}秒后重发` : (isSendingCode ? '发送中...' : '获取验证码') }}
              </button>
            </div>
            <div class="error-tip" v-if="forgetErrors.code">{{ forgetErrors.code }}</div>
          </div>
          <div class="modal-actions">
            <button class="cancel-btn" @click="closeForgetModal">取消</button>
            <button class="next-btn" @click="handleNextStep">下一步</button>
          </div>
        </div>

        <!-- 步骤2：重置密码 -->
        <div v-if="forgetStep === 2" class="forget-step-content">
          <div class="form-item">
            <label class="form-label">新密码 <span class="required">*</span></label>
            <input type="password" class="form-input" v-model="forgetForm.newPwd" placeholder="请输入6位新提现密码">
            <div class="error-tip" v-if="forgetErrors.newPwd">{{ forgetErrors.newPwd }}</div>
          </div>
          <div class="form-item">
            <label class="form-label">确认密码 <span class="required">*</span></label>
            <input type="password" class="form-input" v-model="forgetForm.confirmPwd" placeholder="请再次输入新提现密码">
            <div class="error-tip" v-if="forgetErrors.confirmPwd">{{ forgetErrors.confirmPwd }}</div>
          </div>
          <div class="modal-actions">
            <button class="cancel-btn" @click="closeForgetModal">取消</button>
            <button class="next-btn" @click="handleSaveNewPwd">确认</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 选项卡导航 -->
    <div class="tab-nav">
      <button class="tab-btn" :class="{ active: activeTab === 'apply' }"
        @click="checkAuthThenSwitch('apply')">提现申请</button>
      <button class="tab-btn" :class="{ active: activeTab === 'record' }"
        @click="checkAuthThenSwitch('record')">提现记录</button>
      <button class="tab-btn" :class="{ active: activeTab === 'account' }" @click="checkAuthThenSwitch('account')"
        v-if="!hasWithdrawAccount">设置提现账号</button>
      <button class="tab-btn" :class="{ active: activeTab === 'account' }" @click="checkAuthThenSwitch('account')"
        v-else>修改提现账号</button>
    </div>

    <!-- 1. 提现申请表单 -->
    <div class="form-container" v-if="activeTab === 'apply' && isAuthenticated">
      <!-- 可提现金额卡片 -->
      <div class="amount-card">
        <div class="card-title">可提现金额</div>
        <div class="amount-value">¥{{ userInfo.withdrawCash }}</div>
      </div>

      <form class="withdraw-form">
        <!-- 提现金额 -->
        <div class="form-item">
          <label class="form-label">提现金额 <span class="required">*</span></label>
          <div class="form-desc">
            <span>最低提现金额: ¥100.00</span>
            <button type="button" class="quick-amount" @click="fillAmount(userInfo.withdrawCash)">全部提现</button>
          </div>
          <div class="amount-input-group">
            <span class="amount-unit">¥</span>
            <input type="number" class="form-input amount-input" v-model="WithdrawalRequestTable.amount"
              placeholder="请输入提现金额" step="0.01" min="100">
          </div>
          <div class="error-tip" v-if="withdrawErrors.amount">{{ withdrawErrors.amount }}</div>
        </div>

        <!-- 提现账号 -->
        <div class="form-item">
          <label class="form-label">提现账号 <span class="required">*</span></label>
          <div class="form-desc" v-if="hasWithdrawAccount">当前使用: {{ accountInfo.accountType }} ({{
            maskAccount(accountInfo.confirmAccount) }})</div>
          <div class="form-desc" v-else>
            <span class="warning">请先</span>
            <a href="#" class="link" @click.prevent="checkAuthThenSwitch('account')">设置提现账号</a>
          </div>
          <select class="form-input" v-model="WithdrawalRequestTable.withdrawalId" v-if="hasWithdrawAccount">
            <option :value="accountInfo.id">{{ accountInfo.accountType }} ({{ maskAccount(accountInfo.confirmAccount)
            }})
            </option>
          </select>
          <div class="error-tip" v-if="withdrawErrors.withdrawalId">{{ withdrawErrors.withdrawalId }}</div>
        </div>

        <!-- 提现密码 -->
        <div class="form-item">
          <label class="form-label">提现密码 <span class="required">*</span></label>
          <div class="form-desc">
            <a href="#" class="link" @click.prevent="openForgetModal">忘记密码？</a>
          </div>
          <input type="password" class="form-input" v-model="WithdrawalRequestTable.transactionPwd"
            placeholder="请输入提现密码">
          <div class="error-tip" v-if="withdrawErrors.transactionPwd">{{ withdrawErrors.transactionPwd }}</div>
        </div>

        <!-- 备注 -->
        <div class="form-item">
          <label class="form-label">备注</label>
          <input type="text" class="form-input" v-model="WithdrawalRequestTable.notes" placeholder="可选，最多50字"
            maxlength="50">
        </div>

        <!-- 手续费说明 -->
        <div class="fee-info">
          <div class="fee-title">手续费说明</div>
          <div class="fee-rules">
            <p>1. 个人授权：平台手续费 20%</p>
            <p>2. 企业授权 / 企业拓展授权：平台手续费 40%</p>
            <p>3.充值金额不能提现，可提现金额来自您的收入。</p>
            <p>4.满100元即可提现，每次至少100元，每月限提1次。</p>
            <p>5.提现将转入您设置的支付宝/银行卡账号。</p>
            <p>6.为确保顺利到账，请在支付宝隐私设置中开启“通过邮箱/手机号找到我”。</p>
            <p>7.提交申请后，我们会在3–5个工作日内完成审核。</p>
            <p>温馨提示：页面展示的可提现金额，已扣除对应手续费。</p>
          </div>
        </div>

        <!-- 提交按钮 -->
        <button class="submit-btn" @click.prevent="submitWithdraw">
          提交提现申请
        </button>
      </form>
    </div>

    <!-- 2. 提现记录 -->
    <div class="record-container" v-if="activeTab === 'record' && isAuthenticated">
      <!-- 筛选栏 -->
      <div class="filter-bar">
        <div class="status-filter">
          <button class="status-btn" :class="{ active: filterStatus === 'all' }" @click="handleStatusFilter('all')">
            全部
          </button>
          <button class="status-btn" :class="{ active: filterStatus === '审核中' }" @click="handleStatusFilter('审核中')">
            审核中
          </button>
          <button class="status-btn" :class="{ active: filterStatus === '提现成功' }" @click="handleStatusFilter('提现成功')">
            提现成功
          </button>
          <button class="status-btn" :class="{ active: filterStatus === '已拒绝' }" @click="handleStatusFilter('已拒绝')">
            已拒绝
          </button>
        </div>
        <div class="date-filter">
          <input type="date" class="date-input" v-model="startDate">
          <span class="date-separator">至</span>
          <input type="date" class="date-input" v-model="endDate">
          <button class="search-btn" @click="searchRecord">查询</button>
        </div>
      </div>

      <!-- 记录表格 -->
      <div class="record-table">
        <table>
          <thead>
            <tr>
              <th>申请时间</th>
              <th>提现金额</th>
              <th>到账金额</th>
              <th>提现账号</th>
              <th>账户类型</th>
              <th>真实姓名</th>
              <th>状态</th>
              <th>审核时间</th>
              <th>备注</th>
            </tr>
          </thead>
          <tbody>
            <!-- 加载中提示 -->
            <tr v-if="loadingRecord">
              <td colspan="10" class="empty-cell">加载中...</td>
            </tr>
            <!-- 无数据提示 -->
            <tr v-else-if="total === 0">
              <td colspan="10" class="empty-cell">暂无提现记录</td>
            </tr>
            <!-- 数据列表 -->
            <tr v-else v-for="record in withdrawRecordsList" :key="record.id">
              <td>{{ formatTime(record.applyTime) }}</td>
              <td>¥{{ parseFloat(record.amount).toFixed(2) }}</td>
              <td>¥{{ parseFloat(record.accountAmount).toFixed(2) }}</td>
              <td>{{ maskAccount(record.confirmAccount) }}</td>
              <td>{{ record.accountType }}</td>
              <td>{{ record.realName }}</td>
              <td>
                <span class="status-tag" :class="getStatusClass(record.amountStatus)">
                  {{ record.amountStatus }}
                </span>
              </td>
              <td>{{ formatTime(record.reviewTime) }}</td>
              <td>{{ record.notes || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container" v-if="total > 0 && !loadingRecord">
        <el-pagination @current-change="handleCurrentChange" @size-change="handleSizeChange" :current-page="pageIndex"
          :page-sizes="[10, 20, 50]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
          :total="total" background>
        </el-pagination>
      </div>
    </div>

    <!-- 3. 设置/修改提现账号区域 -->
    <div class="form-container" v-if="activeTab === 'account' && isAuthenticated">
      <!-- 已设置提现账号时显示卡片 -->
      <div class="account-card" v-if="hasWithdrawAccount && !isEditingAccount">
        <div class="card-header">
          <div class="card-title">当前提现账号</div>
          <button class="edit-btn" @click="startEditAccount">修改提现账号</button>
        </div>
        <div class="card-body">
          <div class="account-item">
            <label>账号类型：</label>
            <span>{{ accountInfo.accountType || '未填写' }}</span>
          </div>
          <div class="account-item">
            <label>真实姓名：</label>
            <span>{{ accountInfo.realName || '未填写' }}</span>
          </div>
          <div class="account-item">
            <label>提现账号：</label>
            <span>{{ maskAccount(accountInfo.confirmAccount) }}</span>
          </div>
          <div class="account-item" v-if="accountInfo.accountType === '银行卡'">
            <label>开户行：</label>
            <span>{{ accountInfo.bankAccount || '未填写' }}</span>
          </div>
        </div>
        <div class="card-footer">
          <p class="tips">修改提现账号需要重新验证提现密码，请确保信息准确</p>
        </div>
      </div>

      <!-- 设置/修改提现账号表单 -->
      <form class="withdraw-form" v-if="!hasWithdrawAccount || isEditingAccount">
        <!-- 账号类型 -->
        <div class="form-item">
          <label class="form-label">账号类型 <span class="required">*</span></label>
          <select class="form-input" v-model="setWithdrawRecordForm.accountType">
            <option value="">请选择账号类型</option>
            <option value="支付宝">支付宝</option>
            <option value="银行卡">银行卡</option>
          </select>
          <div class="error-tip" v-if="accountErrors.accountType">{{ accountErrors.accountType }}</div>
        </div>

        <!-- 真实姓名 / 户名 动态切换 -->
        <div class="form-item">
          <label class="form-label">
            {{ setWithdrawRecordForm.accountType === '银行卡' ? '户名' : '真实姓名' }}
            <span class="required">*</span>
          </label>
          <!-- <div class="form-desc">此姓名要与你认证的真实姓名保持一致</div> -->
          <input type="text" class="form-input" v-model="setWithdrawRecordForm.realName" placeholder="请输入">
          <div class="error-tip" v-if="accountErrors.realName">{{ accountErrors.realName }}</div>
        </div>

        <!-- 账号 / 卡号 动态切换 -->
        <div class="form-item">
          <label class="form-label">
            {{ setWithdrawRecordForm.accountType === '银行卡' ? '卡号' : '账号' }}
            <span class="required">*</span>
          </label>
          <input type="text" class="form-input" v-model="setWithdrawRecordForm.confirmAccount" placeholder="请输入">
          <div class="error-tip" v-if="accountErrors.confirmAccount">{{ accountErrors.confirmAccount }}</div>
        </div>

        <!-- 开户行（仅银行卡显示） -->
        <div class="form-item" v-if="setWithdrawRecordForm.accountType === '银行卡'">
          <label class="form-label">开户行 <span class="required">*</span></label>
          <input type="text" class="form-input" v-model="setWithdrawRecordForm.bankAccount" placeholder="请输入开户行名称">
          <div class="error-tip" v-if="accountErrors.bankAccount">{{ accountErrors.bankAccount }}</div>
        </div>

        <!-- 提现密码 -->
        <div class="form-item">
          <label class="form-label">提现密码 <span class="required">*</span></label>
          <div class="form-desc">
            <a href="#" class="link" @click.prevent="openForgetModal">忘记密码？</a>
            <span>|</span>
            <span>如首次使用请先设置提现密码</span>
          </div>
          <input type="password" class="form-input" v-model="setWithdrawRecordForm.transactionPwd"
            placeholder="请输入提现密码">
          <div class="error-tip" v-if="accountErrors.transactionPwd">{{ accountErrors.transactionPwd }}</div>
        </div>

        <!-- 操作按钮 -->
        <div class="form-actions" style="margin-top: 15px; display: flex; gap: 10px;">
          <button class="cancel-btn" @click.prevent="cancelEditAccount" v-if="isEditingAccount">
            取消
          </button>
          <button class="submit-btn" @click.prevent="saveAccount">
            {{ hasWithdrawAccount ? '保存修改' : '提交' }}
          </button>
        </div>
      </form>
    </div>

    <!-- 未认证提示 -->
    <div class="unauth-tip" v-if="!isAuthenticated && activeTab !== ''">
      <div class="tip-content">
        <i class="tip-icon"></i>
        <p>请先完成实名认证，才能进行提现相关操作</p>
        <button class="tip-btn" @click="goToAuthPage">去实名认证</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WithdrawAccountPage',
  data() {
    return {
      // 提现记录（当前页数据）
      withdrawRecordsList: [],
      // 设置提现账号表单（新增 bankAccount）
      setWithdrawRecordForm: {
        realName: "",
        accountType: "",
        confirmAccount: "",
        bankAccount: "",
        transactionPwd: ""
      },
      // 提现申请表单
      WithdrawalRequestTable: {
        amount: "",
        withdrawalId: "",
        transactionPwd: "",
        notes: "",
      },
      // 选项卡状态
      activeTab: 'apply',

      // 账户金额信息
      totalBalance: 0,
      frozenAmount: 0,
      availableAmount: 0,

      // 表单错误信息
      withdrawErrors: {},
      accountErrors: {},

      // 验证码相关
      isSendingCode: false,
      countdown: 0,
      accountForm: {
        code: ''
      },

      // 已保存的账号信息
      accountInfo: {},

      // 提现记录筛选条件
      filterStatus: 'all',
      startDate: '',
      endDate: '',

      // 分页相关参数
      pageIndex: 1,
      pageSize: 10,
      total: 0,

      // 用户信息
      userInfo: {},

      // 实名认证相关
      isAuthenticated: false,
      showAuthModal: false,
      loadingRecord: false,

      // 是否有提现账号
      hasWithdrawAccount: false,
      // 是否处于编辑账号状态
      isEditingAccount: false,

      // 忘记密码弹窗相关
      showForgetPwdModal: false,
      forgetStep: 1,
      forgetForm: {
        phone: '',
        code: '',
        newPwd: '',
        confirmPwd: ''
      },
      forgetErrors: {},
      countdownTimer: null
    }
  },
  async mounted() {
    await this.checkAuthStatus();
    if (this.activeTab === 'record') {
      this.getWithdrawRecord();
    }
    await this.getAccountInfo();
    this.UserHomepage();
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  },
  methods: {
    // 打开忘记密码弹窗
    async openForgetModal() {
      this.showForgetPwdModal = true;
      this.forgetStep = 1;
      this.forgetForm = { phone: '', code: '', newPwd: '', confirmPwd: '' };
      this.forgetErrors = {};
      this.countdown = 0;
      this.isSendingCode = false;
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      await this.queryCurrentPhone();
    },
    // 关闭忘记密码弹窗
    closeForgetModal() {
      this.showForgetPwdModal = false;
      this.forgetStep = 1;
      this.forgetForm = { phone: '', code: '', newPwd: '', confirmPwd: '' };
      this.forgetErrors = {};
      this.countdown = 0;
      this.isSendingCode = false;
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    // 查询当前登录手机号
    async queryCurrentPhone() {
      try {
        const res = await this.$apiFun.getQueryPhone({});
        if (res.status === 200 && res.obj?.userPhone) {
          this.forgetForm.phone = res.obj.userPhone;
        } else {
          this.$message.error(res.msg || '获取手机号失败');
          this.closeForgetModal();
        }
      } catch (err) {
        console.error('获取手机号失败:', err);
        this.$message.error('获取手机号失败，请稍后重试');
        this.closeForgetModal();
      }
    },
    // 发送验证码
    async handleSendCode() {
      if (!this.forgetForm.phone) {
        this.$message.warning('未获取到手机号');
        return;
      }
      this.isSendingCode = true;
      try {
        const res = await this.$apiFun.getSendCodeV3({ PhoneNumbers: this.forgetForm.phone });
        if (res.status === 200) {
          this.$message.success('验证码发送成功');
          this.countdown = 60;
          if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
          }
          this.countdownTimer = setInterval(() => {
            this.countdown--;
            if (this.countdown <= 0) {
              clearInterval(this.countdownTimer);
              this.countdownTimer = null;
              this.isSendingCode = false;
            }
          }, 1000);
        } else {
          this.$message.error(res.msg || '验证码发送失败');
          this.isSendingCode = false;
        }
      } catch (err) {
        console.error('发送验证码失败:', err);
        this.$message.error('验证码发送失败，请稍后重试');
        this.isSendingCode = false;
      }
    },
    // 下一步（校验验证码）
    async handleNextStep() {
      if (!this.validateForgetStep1()) return;
      try {
        const res = await this.$apiFun.getVerificationV3({ code: this.forgetForm.code });
        if (res.status === 200) {
          this.forgetStep = 2;
          this.forgetErrors = {};
        } else {
          this.$message.error(res.msg || '验证码校验失败');
        }
      } catch (err) {
        console.error('校验验证码失败:', err);
        this.$message.error('验证码校验失败，请稍后重试');
      }
    },
    // 保存新密码
    async handleSaveNewPwd() {
      if (!this.validateForgetStep2()) return;
      try {
        const res = await this.$apiFun.postVerificationV2({
          newPwd: this.forgetForm.newPwd,
          confirmPwd: this.forgetForm.confirmPwd
        });
        if (res.status === 200) {
          this.$message.success('提现密码重置成功');
          this.closeForgetModal();
        } else {
          this.$message.error(res.msg || '密码重置失败');
        }
      } catch (err) {
        console.error('重置密码失败:', err);
        this.$message.error('密码重置失败，请稍后重试');
      }
    },
    // 验证步骤1
    validateForgetStep1() {
      const errors = {};
      if (!this.forgetForm.code) {
        errors.code = '请输入验证码';
      }
      this.forgetErrors = errors;
      return Object.keys(errors).length === 0;
    },
    // 验证步骤2
    validateForgetStep2() {
      const errors = {};
      if (!this.forgetForm.newPwd) {
        errors.newPwd = '请输入新密码';
      } else if (this.forgetForm.newPwd.length < 6) {
        errors.newPwd = '新密码长度不能少于6位';
      }
      if (!this.forgetForm.confirmPwd) {
        errors.confirmPwd = '请确认新密码';
      } else if (this.forgetForm.newPwd !== this.forgetForm.confirmPwd) {
        errors.confirmPwd = '两次输入的密码不一致';
      }
      this.forgetErrors = errors;
      return Object.keys(errors).length === 0;
    },

    async checkAuthStatus() {
      try {
        const res = await this.$apiFun.getSeleMerchantCA();
        if (res.status === 200) {
          this.isAuthenticated = !!res.obj.merchantCA;
          if (!this.isAuthenticated) {
            this.showAuthModal = true;
          } else {
            if (this.activeTab === 'record') {
              this.getWithdrawRecord();
            }
          }
        } else {
          this.showAuthModal = true;
          this.$message.error("认证状态查询失败");
          this.isAuthenticated = false;
        }
      } catch (err) {
        this.showAuthModal = true;
        this.$message.error('认证状态查询失败');
        console.error('查询认证状态出错：', err);
        this.isAuthenticated = false;
      }
    },

    goToAuthPage() {
      this.showAuthModal = false;
      this.$router.push('/dashboard/seller/info');
    },

    checkAuthThenSwitch(tab) {
      this.activeTab = tab;
      if (!this.isAuthenticated) {
        this.showAuthModal = true;
        return;
      }
      if (tab === 'record') {
        this.filterStatus = 'all';
        this.pageIndex = 1;
        this.getWithdrawRecord();
      }
      if (tab === 'account') {
        this.isEditingAccount = false;
      }
    },

    startEditAccount() {
      this.setWithdrawRecordForm = {
        realName: this.accountInfo.realName || "",
        accountType: this.accountInfo.accountType || "",
        confirmAccount: this.accountInfo.confirmAccount || "",
        bankAccount: this.accountInfo.bankAccount || "",
        transactionPwd: ""
      };
      this.accountForm.code = '';
      this.isEditingAccount = true;
    },

    cancelEditAccount() {
      this.setWithdrawRecordForm = {
        realName: "",
        accountType: "",
        confirmAccount: "",
        bankAccount: "",
        transactionPwd: ""
      };
      this.accountForm.code = '';
      this.isEditingAccount = false;
      this.accountErrors = {};
    },

    async UserHomepage() {
      try {
        const res = await this.$apiFun.getSellerHomepage();
        if (res.status === 200) {
          this.userInfo = res.obj;
        }
      } catch (err) {
        console.error('商户信息加载失败:', err);
        this.$message?.error('商户信息加载失败，请稍后重试');
      }
    },

    handleCurrentChange(val) {
      this.pageIndex = val;
      this.getWithdrawRecord();
    },

    handleSizeChange(val) {
      this.pageSize = val;
      this.pageIndex = 1;
      this.getWithdrawRecord();
    },

    async getWithdrawRecord() {
      if (!this.isAuthenticated) return;
      this.loadingRecord = true;
      const params = {
        amountStatus: this.filterStatus === 'all' ? "" : this.filterStatus,
        start: this.startDate,
        end: this.endDate,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize
      };
      try {
        const res = await this.$apiFun.getWithdrawRecord(params);
        if (res.status === 200) {
          this.withdrawRecordsList = res.obj || [];
          this.total = res.num || 0;
        } else {
          this.$message.error(res.msg || '提现记录查询失败');
          this.withdrawRecordsList = [];
          this.total = 0;
        }
      } catch (err) {
        console.error('提现记录查询失败:', err);
        this.$message.error('提现记录查询失败，请稍后重试');
        this.withdrawRecordsList = [];
        this.total = 0;
      } finally {
        this.loadingRecord = false;
      }
    },

    async saveAccount() {
      if (!this.isAuthenticated) {
        this.$message.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }
      if (!this.validateAccountForm()) {
        return;
      }
      try {
        const res = await this.$apiFun.postWithdrawal(this.setWithdrawRecordForm);
        if (res.status === 200) {
          this.accountInfo = {
            ...this.accountInfo,
            realName: this.setWithdrawRecordForm.realName,
            accountType: this.setWithdrawRecordForm.accountType,
            confirmAccount: this.setWithdrawRecordForm.confirmAccount,
            bankAccount: this.setWithdrawRecordForm.bankAccount,
            id: this.accountInfo.id || res.obj?.id
          };
          this.hasWithdrawAccount = !!this.accountInfo.confirmAccount;
          this.$message.success(this.hasWithdrawAccount ? '提现账号修改成功' : '提现账号设置成功');
          this.setWithdrawRecordForm = {
            realName: "",
            accountType: "",
            confirmAccount: "",
            bankAccount: "",
            transactionPwd: ""
          };
          this.accountForm.code = '';
          this.isEditingAccount = false;
          this.activeTab = 'apply';
        } else {
          this.$message.error(res.msg || (this.hasWithdrawAccount ? '提现账号修改失败' : '提现账号设置失败'));
        }
      } catch (err) {
        console.error('提现账号操作失败:', err);
        this.$message.error((this.hasWithdrawAccount ? '提现账号修改失败' : '提现账号设置失败') + '，请稍后重试');
      }
    },

    async submitWithdraw() {
      if (!this.isAuthenticated) {
        this.$message.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }
      if (!this.validateWithdrawForm()) {
        return;
      }
      try {
        const res = await this.$apiFun.postWithdrawalRequest({
          withdrawalId: this.accountInfo.confirmAccount,
          amount: this.WithdrawalRequestTable.amount,
          transactionPwd: this.WithdrawalRequestTable.transactionPwd,
          notes: this.WithdrawalRequestTable.notes
        });
        if (res.status === 200) {
          this.$message.success('提现申请提交成功，等待审核');
          this.WithdrawalRequestTable = {
            withdrawalId: this.accountInfo.id,
            amount: "",
            transactionPwd: "",
            notes: ""
          };
          this.getWithdrawRecord();
          this.activeTab = 'record';
        } else {
          this.$message.error(res.msg || '提现申请提交失败');
        }
      } catch (err) {
        console.error('提现申请提交失败:', err);
        this.$message.error('提现申请提交失败，请稍后重试');
      }
    },

    async getAccountInfo() {
      try {
        const res = await this.$apiFun.getSeleWithdrawal();
        if (res.status == 200 && res.obj) {
          this.accountInfo = res.obj;
          this.hasWithdrawAccount = !!res.obj.confirmAccount;
          this.WithdrawalRequestTable.withdrawalId = this.accountInfo.id;
        } else {
          this.hasWithdrawAccount = false;
        }
      } catch (err) {
        console.error('获取提现账号信息失败:', err);
        this.hasWithdrawAccount = false;
      }
    },

    handleStatusFilter(status) {
      this.filterStatus = status;
      this.pageIndex = 1;
      this.getWithdrawRecord();
    },

    searchRecord() {
      this.pageIndex = 1;
      this.getWithdrawRecord();
    },

    fillAmount(amount) {
      this.WithdrawalRequestTable.amount = amount;
    },

    validateWithdrawForm() {
      const errors = {};
      const amount = parseFloat(this.WithdrawalRequestTable.amount || 0);
      if (!this.WithdrawalRequestTable.amount || amount < 100) {
        errors.amount = '请输入不低于100元的提现金额';
      } else if (amount > this.userInfo.withdrawCash) {
        errors.amount = '提现金额不能超过可提现金额';
      }
      if (!this.hasWithdrawAccount) {
        errors.withdrawalId = '请先设置提现账号';
      }
      if (!this.WithdrawalRequestTable.transactionPwd) {
        errors.transactionPwd = '请输入提现密码';
      }
      this.withdrawErrors = errors;
      return Object.keys(errors).length === 0;
    },

    // 账号表单验证（新增开户行校验）
    validateAccountForm() {
      const errors = {};
      if (!this.setWithdrawRecordForm.realName) {
        errors.realName = '请输入';
      }
      if (!this.setWithdrawRecordForm.accountType) {
        errors.accountType = '请选择账号类型';
      }
      if (!this.setWithdrawRecordForm.confirmAccount) {
        errors.confirmAccount = '请输入';
      } else {
        // 支付宝格式校验
        if (this.setWithdrawRecordForm.accountType === '支付宝') {
          const phoneReg = /^1[3-9]\d{9}$/;
          const emailReg = /^[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]+)+$/;
          if (!phoneReg.test(this.setWithdrawRecordForm.confirmAccount) && !emailReg.test(this.setWithdrawRecordForm.confirmAccount)) {
            errors.confirmAccount = '支付宝账号格式错误（手机号或邮箱）';
          }
        }
        // 银行卡格式校验
        if (this.setWithdrawRecordForm.accountType === '银行卡') {
          const bankReg = /^\d{16,19}$/;
          if (!bankReg.test(this.setWithdrawRecordForm.confirmAccount)) {
            errors.confirmAccount = '银行卡号格式错误（16-19位数字）';
          }
        }
      }
      // 开户行必填校验
      if (this.setWithdrawRecordForm.accountType === '银行卡' && !this.setWithdrawRecordForm.bankAccount) {
        errors.bankAccount = '请输入开户行名称';
      }
      if (!this.setWithdrawRecordForm.transactionPwd) {
        errors.transactionPwd = '请输入提现密码';
      } else if (this.setWithdrawRecordForm.transactionPwd.length < 6) {
        errors.transactionPwd = '提现密码长度不能少于6位';
      }
      this.accountErrors = errors;
      return Object.keys(errors).length === 0;
    },

    getCode() { },

    viewDetail(record) {
      this.$message.info(`查看提现ID为${record.id}的详情`);
    },

    cancelWithdraw(record) {
      if (confirm('确定要取消此提现申请吗？')) {
        record.amountStatus = '已拒绝';
        this.$message.success('提现申请已取消');
        this.total = Math.max(0, this.total - 1);
        if (this.withdrawRecordsList.length === 1 && this.pageIndex > 1) {
          this.pageIndex -= 1;
          this.getWithdrawRecord();
        }
      }
    },

    maskAccount(account) {
      if (!account) return '';
      if (/^1[3-9]\d{9}$/.test(account)) {
        return account.replace(/^(\d{3})(\d{4})(\d{4})$/, '$1****$3');
      }
      if (/^\d{16,19}$/.test(account)) {
        return account.replace(/^(\d{4})(\d+)(\d{4})$/, '$1********$3');
      }
      if (/^[\w-]+@[\w-]+\.[a-z]{2,}$/.test(account)) {
        const [prefix, suffix] = account.split('@');
        return `${prefix.substring(0, 3)}****@${suffix}`;
      }
      return account;
    },

    formatTime(timeStr) {
      if (!timeStr) return '-';
      return timeStr;
    },

    getStatusClass(status) {
      switch (status) {
        case '审核中': return 'pending';
        case '提现成功': return 'success';
        case '已拒绝': return 'failed';
        default: return '';
      }
    }
  }
};
</script>

<style lang="less" scoped>
// 深色主题变量
@bg-primary: #2b2b2b;
@bg-secondary: #111827;
@text-primary: #e0e0e0;
@text-secondary: #a0a0a0;
@accent-red: #0ea5e9;
@border-color: #4d4d4d;
@input-bg: transparent;
@success-color: #00b42a;
@pending-color: #ff7d00;
@failed-color: red;

.withdraw-account-page {
  color: @text-primary;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;
  position: relative;

  // 忘记密码弹窗样式
  .forget-pwd-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    .forget-pwd-content {
      background-color: @bg-secondary;
      border-radius: 8px;
      width: 400px;
      padding: 30px 20px;
      text-align: left;

      .forget-pwd-title {
        font-size: 20px;
        color: @text-primary;
        margin-bottom: 20px;
        font-weight: 500;
        text-align: center;
      }

      .forget-step-content {
        display: flex;
        flex-direction: column;
        gap: 15px;
      }

      .form-item {
        display: flex;
        flex-direction: column;
        gap: 6px;
        width: 100%;

        .form-label {
          font-size: 14px;
          color: @text-primary;
          display: flex;
          align-items: center;

          .required {
            color: @accent-red;
            margin-left: 4px;
            font-size: 16px;
          }
        }

        .form-input {
          width: 100%;
          padding: 10px 12px;
          background-color: @input-bg;
          border: 1px solid @border-color;
          border-radius: 4px;
          color: @text-primary;
          font-size: 14px;
          box-sizing: border-box;

          &::placeholder {
            color: @text-secondary;
          }

          &:focus {
            outline: none;
            border-color: @accent-red;
          }

          &[disabled] {
            background-color: #333;
            color: @text-secondary;
            cursor: not-allowed;
          }
        }

        .error-tip {
          font-size: 12px;
          color: @accent-red;
          margin-top: -4px;
        }
      }

      .code-input-group {
        display: flex;
        gap: 10px;
        width: 100%;

        .code-input {
          flex: 1;
        }

        .code-btn {
          width: 120px;
          background-color: @accent-red;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 10px 0;

          &:not(:disabled):hover {
            background-color: #0b8bbb;
            transform: translateY(-1px);
          }

          &:disabled {
            background-color: #666;
            cursor: not-allowed;
            opacity: 0.7;
            color: #ccc;
          }
        }
      }

      .modal-actions {
        display: flex;
        justify-content: center;
        gap: 10px;
        margin-top: 20px;

        .cancel-btn {
          width: 100px;
          background-color: #666;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 10px 0;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background-color: #555;
          }
        }

        .next-btn {
          width: 100px;
          background-color: @accent-red;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 10px 0;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background-color: #0b8bbb;
          }
        }
      }
    }
  }

  // 实名认证弹窗样式
  .auth-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    .auth-modal-content {
      background-color: @bg-secondary;
      border-radius: 8px;
      width: 400px;
      padding: 30px 20px;
      text-align: center;

      .auth-modal-title {
        font-size: 20px;
        color: @text-primary;
        margin-bottom: 15px;
        font-weight: 500;
      }

      .auth-modal-desc {
        font-size: 14px;
        color: @text-secondary;
        line-height: 1.8;
        margin-bottom: 20px;
      }

      .auth-modal-btn {
        background-color: #0ea5e9;
        color: #fff;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 16px;
        cursor: pointer;
        width: 100%;
        max-width: 200px;
        transition: all 0.2s ease;

        &:hover {
          background-color: #0b8bbb;
        }
      }
    }
  }

  // 提现账号卡片样式
  .account-card {
    background-color: @bg-secondary;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid @border-color;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      padding-bottom: 10px;
      border-bottom: 1px solid @border-color;

      .card-title {
        font-size: 16px;
        font-weight: 500;
        color: @text-primary;
      }

      .edit-btn {
        background-color: @accent-red;
        color: white;
        border: none;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background-color: #0b8bbb;
        }
      }
    }

    .card-body {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .account-item {
        display: flex;
        align-items: center;
        gap: 8px;

        label {
          color: @text-secondary;
          min-width: 80px;
          font-size: 14px;
        }

        span {
          color: @text-primary;
          font-size: 14px;
        }
      }
    }

    .card-footer {
      margin-top: 15px;
      padding-top: 10px;
      border-top: 1px solid @border-color;

      .tips {
        font-size: 12px;
        color: @text-secondary;
        margin: 0;
      }
    }
  }

  // 未认证提示样式
  .unauth-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background-color: @bg-secondary;
    border-radius: 6px;
    padding: 20px;

    .tip-content {
      text-align: center;

      .tip-icon {
        display: inline-block;
        width: 60px;
        height: 60px;
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="%23ff7d00" d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"/><path fill="%23ff7d00" d="M512 140c-205.4 0-372 166.6-372 372s166.6 372 372 372 372-166.6 372-372-166.6-372-372-372zm0 694c-177.5 0-322-144.5-322-322s144.5-322 322-322 322 144.5 322 322-144.5 322-322 322z"/><path fill="%23ff7d00" d="M512 232c-154.6 0-280 125.4-280 280s125.4 280 280 280 280-125.4 280-280-125.4-280-280-280zm0 508c-125.9 0-228-102.1-228-228s102.1-228 228-228 228 102.1 228 228-102.1 228-228 228z"/></svg>') no-repeat center;
        margin-bottom: 20px;
      }

      p {
        font-size: 16px;
        color: @text-secondary;
        margin-bottom: 20px;
      }

      .tip-btn {
        background-color: @accent-red;
        color: #fff;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background-color: #0b8bbb;
        }
      }
    }
  }

  // 选项卡导航
  .tab-nav {
    display: flex;
    gap: 1px;
    margin-bottom: 20px;
    // background-color: @border-color;
    border-radius: 4px;
    overflow: hidden;

    .tab-btn {
      background-color: @bg-secondary;
      border: none;
      padding: 12px 24px;
      font-size: 14px;
      color: @text-secondary;
      cursor: pointer;
      transition: all 0.2s;

      &.active {
        background-color: @bg-primary;
        color: @accent-red;
        font-weight: 500;
      }

      &:hover:not(.active) {
        background-color: #4a4a4a;
        color: @text-primary;
      }
    }
  }

  // 表单容器通用样式
  .form-container {
    max-width: 800px;

    .withdraw-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .form-item {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .form-label {
        font-size: 14px;
        color: @text-primary;
        display: flex;
        align-items: center;

        .required {
          color: @accent-red;
          margin-left: 4px;
          font-size: 16px;
        }
      }

      .form-desc {
        font-size: 12px;
        color: @text-secondary;
        display: flex;
        align-items: center;
        gap: 8px;

        .link {
          color: @accent-red;
          text-decoration: none;
          cursor: pointer;

          &:hover {
            text-decoration: underline;
          }
        }

        .warning {
          color: @pending-color;
        }
      }

      .form-input {
        width: 100%;
        padding: 10px 12px;
        background-color: @input-bg;
        border: 1px solid @border-color;
        border-radius: 4px;
        color: @text-primary;
        font-size: 14px;
        box-sizing: border-box;

        &::placeholder {
          color: @text-secondary;
        }

        &:focus {
          outline: none;
          border-color: @accent-red;
        }

        &[disabled] {
          background-color: #333;
          color: @text-secondary;
          cursor: not-allowed;
        }
      }

      .amount-input-group {
        display: flex;
        align-items: center;

        .amount-unit {
          color: @text-primary;
          padding: 0 8px 0 12px;
          background-color: @input-bg;
          border: 1px solid @border-color;
          border-right: none;
          border-radius: 4px 0 0 4px;
          height: 40px;
          display: flex;
          align-items: center;
        }

        .amount-input {
          border-radius: 0 4px 4px 0 !important;
        }
      }

      .quick-amount {
        margin-left: auto;
        background: transparent;
        border: 1px dashed @accent-red;
        color: @accent-red;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;

        &:hover {
          background-color: rgba(14, 165, 233, 0.1);
        }
      }

      .error-tip {
        font-size: 12px;
        color: @accent-red;
        margin-top: -4px;
      }
    }

    .cancel-btn {
      width: 100px;
      background-color: #666;
      color: white;
      border: none;
      border-radius: 4px;
      padding: 12px 0;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background-color: #555;
      }
    }

    .submit-btn {
      width: 160px;
      background-color: @accent-red;
      color: white;
      border: none;
      border-radius: 4px;
      padding: 12px 0;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.2s;
      align-self: flex-start;

      &:hover:not(:disabled) {
        background-color: #0b8bbb;
      }

      &:disabled {
        background-color: #666;
        cursor: not-allowed;
        opacity: 0.7;
      }
    }

    .amount-card {
      background-color: @bg-secondary;
      border-radius: 6px;
      padding: 15px;
      margin-bottom: 20px;
      border-left: 3px solid @accent-red;

      .card-title {
        font-size: 14px;
        color: @text-secondary;
        margin-bottom: 8px;
      }

      .amount-value {
        font-size: 28px;
        color: @accent-red;
        font-weight: 500;
        margin-bottom: 5px;
      }

      .amount-desc {
        font-size: 12px;
        color: @text-secondary;
        display: flex;
        gap: 10px;
      }
    }

    .fee-info {
      background-color: rgba(255, 125, 0, 0.1);
      border: 1px solid rgba(255, 125, 0, 0.3);
      border-radius: 4px;
      padding: 12px;
      margin-bottom: 10px;

      .fee-title {
        font-size: 14px;
        color: @pending-color;
        margin-bottom: 8px;
        font-weight: 500;
      }

      .fee-rules {
        font-size: 12px;
        color: @text-secondary;
        line-height: 1.6;

        p {
          margin: 4px 0;
        }
      }
    }
  }

  // 提现记录容器
  .record-container {
    .filter-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      flex-wrap: wrap;
      gap: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid @border-color;

      .status-filter {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        .status-btn {
          background-color: @bg-secondary;
          border: 1px solid @border-color;
          color: @text-secondary;
          padding: 5px 12px;
          border-radius: 4px;
          font-size: 13px;
          cursor: pointer;

          &.active {
            background-color: @accent-red;
            color: white;
            border-color: @accent-red;
          }

          &:hover:not(.active) {
            border-color: #666;
          }
        }
      }

      .date-filter {
        display: flex;
        align-items: center;
        gap: 10px;

        .date-input {
          background-color: @input-bg;
          border: 1px solid @border-color;
          color: @text-primary;
          padding: 5px 8px;
          border-radius: 4px;
          font-size: 13px;

          &:focus {
            outline: none;
            border-color: @accent-red;
          }
        }

        .date-separator {
          color: @text-secondary;
        }

        .search-btn {
          background-color: @accent-red;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 13px;
          cursor: pointer;
          padding: 5px 12px;

          &:hover {
            background-color: #0b8bbb;
          }
        }
      }
    }

    .record-table {
      width: 100%;
      overflow-x: auto;

      table {
        width: 100%;
        border-collapse: collapse;
        min-width: 1000px;

        th {
          background-color: @bg-secondary;
          color: @text-primary;
          font-weight: 500;
          padding: 12px 10px;
          text-align: left;
          border: 1px solid @border-color;
          font-size: 14px;
        }

        td {
          padding: 12px 10px;
          border: 1px solid @border-color;
          font-size: 13px;
          color: @text-primary;

          &.empty-cell {
            text-align: center;
            color: @text-secondary;
            padding: 30px 0;
          }
        }

        tr:nth-child(even) {
          background-color: #333333;
        }

        tr:hover {
          background-color: #3a3a3a;
        }
      }
    }

    .status-tag {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;

      &.pending {
        background-color: rgba(255, 125, 0, 0.2);
        color: @pending-color;
      }

      &.success {
        background-color: rgba(0, 180, 42, 0.2);
        color: @success-color;
      }

      &.failed {
        background-color: rgba(211, 19, 19, 0.2);
        color: @failed-color;
      }
    }

    .detail-btn {
      background: transparent;
      border: none;
      color: @accent-red;
      cursor: pointer;
      font-size: 13px;
      padding: 2px 5px;

      &:hover {
        text-decoration: underline;
      }
    }

    .cancel-btn {
      background: transparent;
      border: none;
      color: @pending-color;
      cursor: pointer;
      font-size: 13px;
      padding: 2px 5px;
      margin-left: 10px;

      &:hover {
        text-decoration: underline;
      }
    }

    .pagination-container {
      margin-top: 30px;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 10px 0;
    }

    :deep(.el-pagination) {
      background-color: transparent !important;

      .el-pagination__total {
        color: #e0e0e0 !important;
        font-size: 14px !important;
      }

      .el-pagination__sizes {
        color: #e0e0e0 !important;

        .el-input__inner {
          background-color: #080a12 !important;
          color: #e0e0e0 !important;
          border: 1px solid #424242 !important;

          &:focus {
            border-color: #0ea5e9 !important;
          }
        }
      }

      .el-pager {
        li {
          background-color: #080a12 !important;
          color: #a0a0a0 !important;
          border: 1px solid #424242 !important;
          margin: 0 2px !important;

          &:hover {
            background-color: rgba(255, 255, 255, 0.1) !important;
            color: #0ea5e9 !important;
            border-color: #0ea5e9 !important;
          }

          &.active {
            background-color: #0ea5e9 !important;
            color: #ffffff !important;
            border-color: #0ea5e9 !important;
          }
        }
      }

      .btn-prev,
      .btn-next {
        background-color: @bg-secondary !important;
        color: @text-secondary !important;
        border: 1px solid @border-color !important;
        min-width: 30px !important;
        margin: 0 2px !important;

        &.disabled {
          background-color: darken(@bg-secondary, 5%) !important;
          color: darken(@text-secondary, 20%) !important;
          border-color: darken(@border-color, 10%) !important;
          cursor: not-allowed !important;
        }

        &:not(.disabled):hover {
          background-color: @bg-primary !important;
          color: @accent-red !important;
          border-color: @accent-red !important;
        }
      }

      .el-pagination__jump {
        color: #a0a0a0 !important;

        .el-input__inner {
          background-color: #080a12 !important;
          color: #e0e0e0 !important;
          border: 1px solid #424242 !important;

          &:focus {
            border-color: #0ea5e9 !important;
          }
        }
      }
    }
  }

  // 响应式适配
  @media (max-width: 768px) {
    padding: 10px;

    .record-container {
      .filter-bar {
        flex-direction: column;
        align-items: flex-start;

        .date-filter {
          width: 100%;
          justify-content: space-between;
        }
      }

      :deep(.el-pagination) {
        justify-content: center;
        flex-wrap: wrap;

        .el-pagination__total,
        .el-pagination__sizes {
          width: 100%;
          margin-bottom: 10px;
          text-align: center;
        }
      }
    }

    .forget-pwd-modal {
      .forget-pwd-content {
        width: 90%;
        max-width: 400px;
        padding: 20px 15px;

        .modal-actions {
          flex-direction: column;
          align-items: center;

          .cancel-btn,
          .next-btn {
            width: 100%;
            max-width: 200px;
          }
        }
      }
    }

    .auth-modal {
      .auth-modal-content {
        width: 90%;
        max-width: 400px;
        padding: 20px 15px;
      }
    }
  }
}
</style>